<template>
  <div class="ci-summary-group">

    <!-- ── Header ─────────────────────────────────────────────────────── -->
    <div class="ci-summary-title ci-card-title ci-accent ci-accent--ctc">
    <div class="ci-summary-head-title">swiftCTC</div>
        <span class="ci-kpi-row">
        <span class="ci-kpi-value">{{ rows.length }}</span>
        <span class="ci-kpi-label">loans</span>
      </span>
      <div class="ci-summary-head">
        
        <select class="ci-trend-select" v-model="selectedTimeframe">
          <option v-for="opt in timeframeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
      </div>
    </div>

    <!-- ── Body ───────────────────────────────────────────────────────── -->
    <div class="ci-summary-cards ci-scroll">
      <div v-if="loading" class="ci-summary-loading">Loading...</div>
      <div v-else-if="error" class="ci-empty-inline ci-empty-inline--error">{{ error }}</div>

      <template v-else>
        <div v-if="!groupedUws.length" class="ci-empty-inline">No CTC runs in this range</div>

        <!-- ── UW group rows ─────────────────────────────────────────── -->
        <div
          v-for="uw in groupedUws"
          :key="uw.uwId"
          class="ci-uw-group"
        >
          <!-- UW header — click to expand/collapse loan list -->
          <button
            class="ci-uw-header"
            type="button"
            @click="toggleUw(uw.uwId)"
          >
            <div class="ci-uw-left">
              <span class="ci-uw-chevron" :class="{ 'ci-uw-chevron--open': expandedUws[uw.uwId] }">›</span>
              <span class="ci-uw-name">{{ uw.displayName }}</span>
            </div>
            <div class="ci-uw-pills">
              <span v-if="uw.counts.FHA"          class="ci-type-pill ci-type-pill--fha">FHA {{ uw.counts.FHA }}</span>
              <span v-if="uw.counts.Conventional" class="ci-type-pill ci-type-pill--conv">Conv {{ uw.counts.Conventional }}</span>
              <span v-if="uw.counts.USDA"         class="ci-type-pill ci-type-pill--usda">USDA {{ uw.counts.USDA }}</span>
              <span v-if="uw.counts.VA"            class="ci-type-pill ci-type-pill--va">VA {{ uw.counts.VA }}</span>
              <span v-if="uw.counts.Other"         class="ci-type-pill ci-type-pill--other">Other {{ uw.counts.Other }}</span>
              <span class="ci-uw-total">{{ uw.loans.length }}</span>
            </div>
          </button>

          <!-- Loan rows — shown when UW expanded, capped + scrollable -->
          <div v-if="expandedUws[uw.uwId]" class="ci-uw-loans">
            <button
              v-for="r in uw.loans"
              :key="r.loanId"
              class="ci-loan-row"
              type="button"
              @click="openLoan(r)"
            >
              <div class="ci-loan-left">
                <span class="ci-loan-borrower">{{ r.borrowerName || r.loanNumber || r.loanId }}</span>
                <div class="ci-loan-meta">
                  <span class="ci-type-pill" :class="loanTypePillClass(r.loanType)">{{ r.loanType || '—' }}</span>
                  <span class="ci-loan-sub">{{ formatDate(r.processedAt) }}</span>
                </div>
              </div>
              <div class="ci-metric-pill">
                <span class="ci-pill-label">Conds</span>
                <span class="ci-pill-value">{{ r.conditionsAdded }}</span>
              </div>
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- ── Loan modal ────────────────────────────────────────────────── -->
    <div v-if="showModal && selectedRow" class="ci-modal" @click.self="closeLoan">
      <div class="ci-modal__panel ci-modal__panel--wide">
        <div class="ci-modal__header">
          <div class="ci-modal__head-left">
            <div class="ci-modal__title ci-card-title">{{ selectedRow.borrowerName || selectedRow.loanNumber }}</div>
            <div class="ci-modal__meta">
              <span class="ci-sub-pill"><span class="ci-sub-pill__k">Loan #</span><span class="ci-sub-pill__v">{{ selectedRow.loanNumber || '—' }}</span></span>
              <span class="ci-sub-pill"><span class="ci-sub-pill__k">UW</span><span class="ci-sub-pill__v">{{ selectedRow.underwriterDisplayName || '—' }}</span></span>
              <span class="ci-type-pill" :class="loanTypePillClass(selectedRow.loanType)">{{ selectedRow.loanType || '—' }}</span>
              <span class="ci-sub-pill"><span class="ci-sub-pill__k">Run by</span><span class="ci-sub-pill__v">{{ selectedRow.runByDisplayName || selectedRow.runByEmail || 'System' }}</span></span>
              <span class="ci-sub-pill"><span class="ci-sub-pill__k">Date</span><span class="ci-sub-pill__v">{{ formatDate(selectedRow.processedAt) }}</span></span>
            </div>
          </div>
          <div class="ci-modal__head-actions">
            <button class="ci-open-swift" type="button" @click="openInSwift(selectedRow.loanId)">Open in Swift</button>
            <button class="ci-modal__close" type="button" @click="closeLoan">✕</button>
          </div>
        </div>

        <div class="ci-modal__body ci-scroll">
          <div v-if="detailsLoading" class="ci-summary-loading">Loading details...</div>
          <div v-else-if="detailsError" class="ci-empty-inline ci-empty-inline--error">{{ detailsError }}</div>

          <template v-else-if="details">
            <div class="ci-drill-section">
              <div class="ci-drill-section-title">Financials</div>
              <div class="ci-drill-chips">
                <div class="ci-drill-chip" v-if="details.cashToClose"><span class="ci-drill-chip__k">Cash to Close</span><span class="ci-drill-chip__v">{{ formatMoney(details.cashToClose) }}</span></div>
                <div class="ci-drill-chip" v-if="details.monthlyPaymentWithBuffer"><span class="ci-drill-chip__k">Monthly (w/ buffer)</span><span class="ci-drill-chip__v">{{ formatMoney(details.monthlyPaymentWithBuffer) }}</span></div>
                <div class="ci-drill-chip" v-if="details.emd"><span class="ci-drill-chip__k">EMD</span><span class="ci-drill-chip__v">{{ formatMoney(details.emd) }}</span></div>
                <div class="ci-drill-chip" v-if="details.sellerCredit && details.sellerCredit !== '0'"><span class="ci-drill-chip__k">Seller Credit</span><span class="ci-drill-chip__v">{{ formatMoney(details.sellerCredit) }}</span></div>
              </div>
            </div>

            <div class="ci-drill-section" v-if="details.milestoneName">
              <div class="ci-drill-section-title">Milestone</div>
              <div class="ci-drill-chips">
                <div class="ci-drill-chip ci-drill-chip--success"><span class="ci-drill-chip__k">Set</span><span class="ci-drill-chip__v">{{ details.milestoneName }}</span></div>
                <div class="ci-drill-chip" v-if="details.milestoneError"><span class="ci-drill-chip__k">Note</span><span class="ci-drill-chip__v ci-drill-chip__v--note">{{ details.milestoneError }}</span></div>
              </div>
            </div>

            <div class="ci-drill-section" v-if="parsedConditions.length">
              <div class="ci-drill-section-title">Conditions Updated ({{ parsedConditions.length }})</div>
              <div class="ci-detail-list">
                <button
                  v-for="(c, i) in parsedConditions"
                  :key="i"
                  class="ci-detail-card ci-detail-card--compact ci-detail-card--click"
                  type="button"
                  @click="openCondition(c)"
                >
                  <div class="ci-detail-title-row">
                    <span class="ci-detail-title">{{ c.ConditionCode }}</span>
                    <div style="display:flex;align-items:center;gap:6px;">
                      <span class="ci-sub-pill ci-sub-pill--blue"><span class="ci-sub-pill__v">{{ c.Action }}</span></span>
                      <span class="ci-drill-chevron">›</span>
                    </div>
                  </div>
                  <div class="ci-detail-text ci-detail-text--sm">{{ c.ConditionTitle }}</div>
                </button>
              </div>
            </div>

            <div class="ci-drill-section" v-if="parsedDebts.length">
              <div class="ci-drill-section-title">Debts at Closing</div>
              <div class="ci-detail-list">
                <div v-for="(d, i) in parsedDebts" :key="i" class="ci-detail-card ci-detail-card--compact">
                  <div class="ci-detail-title-row">
                    <span class="ci-detail-title">{{ d.CreditorName }}</span>
                    <div class="ci-metric-pill"><span class="ci-pill-label">Balance</span><span class="ci-pill-value">{{ formatMoney(d.Balance) }}</span></div>
                  </div>
                  <div v-if="d.AccountNumber" class="ci-detail-subrow"><span class="ci-sub-pill"><span class="ci-sub-pill__k">Acct</span><span class="ci-sub-pill__v">{{ d.AccountNumber }}</span></span></div>
                </div>
              </div>
            </div>

            <div class="ci-drill-section" v-if="parsedFhaDocs.length">
              <div class="ci-drill-section-title">FHA / VA Docs Signed</div>
              <div class="ci-drill-chips">
                <div v-for="(doc, i) in parsedFhaDocs" :key="i" class="ci-drill-chip ci-drill-chip--doc"><span class="ci-drill-chip__v ci-drill-chip__v--sm">{{ doc }}</span></div>
              </div>
            </div>

            <div class="ci-drill-section" v-if="details.aiSummary">
              <div class="ci-drill-section-title">AI Summary</div>
              <div class="ci-detail-block"><div class="ci-detail-text">{{ details.aiSummary }}</div></div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- ── Condition detail modal ─────────────────────────────────────── -->
    <div v-if="showConditionModal && selectedCondition" class="ci-modal" @click.self="closeCondition">
      <div class="ci-modal__panel">
        <div class="ci-modal__header">
          <div class="ci-modal__head-left">
            <div class="ci-modal__title ci-card-title">{{ selectedCondition.ConditionCode }}</div>
            <div class="ci-modal__meta">
              <span class="ci-sub-pill ci-sub-pill--blue"><span class="ci-sub-pill__v">{{ selectedCondition.Action }}</span></span>
            </div>
          </div>
          <div class="ci-modal__head-actions">
            <button class="ci-modal__close" type="button" @click="closeCondition">✕</button>
          </div>
        </div>
        <div class="ci-modal__body ci-scroll">
          <div class="ci-drill-section">
            <div class="ci-drill-section-title">Title</div>
            <div class="ci-detail-block"><div class="ci-detail-text" style="font-weight:950;">{{ selectedCondition.ConditionTitle || '—' }}</div></div>
          </div>
          <div class="ci-drill-section" v-if="selectedCondition.Description">
            <div class="ci-drill-section-title">Description</div>
            <div class="ci-detail-block"><div class="ci-detail-text">{{ selectedCondition.Description }}</div></div>
          </div>
          <div class="ci-drill-section" v-if="selectedCondition.Error">
            <div class="ci-drill-section-title">Error</div>
            <div class="ci-detail-block"><div class="ci-detail-text ci-drill-chip__v--note">{{ selectedCondition.Error }}</div></div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import ApiService from "@/api/api.service.js"

export default {
  name: "CtcWizardSummaryCard",
  props: {
    authToken: { type: String, default: "" }
  },
  data() {
    return {
      rows: [],
      loading: false,
      error: "",
      showModal: false,
      showConditionModal: false,
      selectedRow: null,
      selectedCondition: null,
      details: null,
      detailsLoading: false,
      detailsError: "",
      expandedUws: {},
      selectedTimeframe: "today",
      timeframeOptions: [
        { value: "today",     label: "Today" },
        { value: "yesterday", label: "Yesterday" },
        { value: "thisWeek",  label: "This week" },
        { value: "lastWeek",  label: "Last week" },
        { value: "thisMonth", label: "This month" },
        { value: "lastMonth", label: "Last month" },
        { value: "rolling90", label: "Rolling 90" }
      ]
    }
  },
  computed: {
    dateRange() {
      const now = new Date()
      const eod = d => { const x = new Date(d); x.setHours(23,59,59,999); return x }
      const sod = d => { const x = new Date(d); x.setHours(0,0,0,0); return x }
      const add = (d,n) => { const x = new Date(d); x.setDate(x.getDate()+n); return x }
      const monStart = d => { const x = sod(d); x.setDate(x.getDate()-((x.getDay()+6)%7)); return x }
      const mo1 = d => { const x = sod(d); x.setDate(1); return x }
      if (this.selectedTimeframe === "today")     return { start: sod(now), end: eod(now) }
      if (this.selectedTimeframe === "yesterday") { const y=add(now,-1); return { start: sod(y), end: eod(y) } }
      if (this.selectedTimeframe === "thisWeek")  return { start: monStart(now), end: eod(now) }
      if (this.selectedTimeframe === "lastWeek")  { const m=monStart(now); return { start: add(m,-7), end: eod(add(m,-1)) } }
      if (this.selectedTimeframe === "thisMonth") return { start: mo1(now), end: eod(now) }
      if (this.selectedTimeframe === "lastMonth") { const s=mo1(add(mo1(now),-1)); return { start: s, end: eod(add(mo1(now),-1)) } }
      return { start: sod(add(now,-89)), end: eod(now) }
    },

    groupedUws() {
      const map = {}
      for (const r of this.rows) {
        const name  = (r.underwriterDisplayName || r.runByDisplayName || r.runByEmail || "").trim()
        const id    = name || "unknown"
        const label = name || "Unknown"
        if (!map[id]) map[id] = { uwId: id, displayName: label, loans: [], counts: {} }
        map[id].loans.push(r)
        const t = this.normalizeLoanType(r.loanType)
        map[id].counts[t] = (map[id].counts[t] || 0) + 1
      }
      return Object.values(map).sort((a,b) => b.loans.length - a.loans.length)
    },

    parsedConditions() { try { return JSON.parse(this.details?.conditionsJson || "[]") || [] } catch { return [] } },
    parsedDebts()      { try { return JSON.parse(this.details?.debtsAtClosingJson || "[]") || [] } catch { return [] } },
    parsedFhaDocs()    { try { return [...new Set(JSON.parse(this.details?.fhaDocsSignedJson || "[]") || [])] } catch { return [] } }
  },
  methods: {
    toIso(d) { return d instanceof Date ? d.toISOString() : null },

    formatDate(v) {
      if (!v) return "—"
      const d = new Date(v)
      if (isNaN(d.getTime())) return "—"
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    },

    formatMoney(v) {
      if (v === null || v === undefined || v === "") return "—"
      const n = parseFloat(String(v).replace(/[$,]/g, ""))
      if (isNaN(n)) return String(v)
      return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    },

    extractRoot(resp) { return resp?.data?.data || resp?.data || resp || {} },

    normalizeLoanType(raw) {
      if (!raw) return "Other"
      const u = raw.trim().toUpperCase()
      if (u === "FHA") return "FHA"
      if (u === "VA")  return "VA"
      if (u.includes("USDA") || u.includes("FARMERSHOME") || u.includes("RHS")) return "USDA"
      if (u.includes("CONV") || u.includes("CONVENTIONAL")) return "Conventional"
      return "Other"
    },

    loanTypePillClass(raw) {
      const t = this.normalizeLoanType(raw)
      return { FHA: "ci-type-pill--fha", VA: "ci-type-pill--va", USDA: "ci-type-pill--usda", Conventional: "ci-type-pill--conv", Other: "ci-type-pill--other" }[t] || "ci-type-pill--other"
    },

    toggleUw(uwId) {
      this.expandedUws = { ...this.expandedUws, [uwId]: !this.expandedUws[uwId] }
    },

    async openLoan(row) {
      this.selectedRow = row
      this.showModal = true
      this.details = null
      this.detailsError = ""
      this.detailsLoading = true
      try {
        const resp = await ApiService.GetCtcWizardDrillIn(row.loanId, this.authToken)
        const root = this.extractRoot(resp)
        this.details = Array.isArray(root?.data) ? root.data[0] : (root?.data || root || null)
      } catch (e) {
        this.detailsError = e?.message || "Failed to load details"
      } finally {
        this.detailsLoading = false
      }
    },

    closeLoan() {
      this.showModal = false
      this.selectedRow = null
      this.details = null
      this.detailsError = ""
    },

    openCondition(condition) {
      this.selectedCondition = condition
      this.showConditionModal = true
    },

    closeCondition() {
      this.showConditionModal = false
      this.selectedCondition = null
    },

    openInSwift(loanId) {
      if (!loanId) return
      const isDev = window.location.hostname === "localhost" || window.location.hostname === "dev.swiftapp.ai"
      window.open(`${isDev ? "https://dev-swift.goldstarfinancial.com" : "https://swift.goldstarfinancial.com"}?id=${loanId}`, "_blank")
    },

    async fetchData() {
      if (!this.authToken) return
      this.loading = true
      this.error = ""
      this.expandedUws = {}
      try {
        const { start, end } = this.dateRange
        const resp = await ApiService.GetCtcWizardSummary(this.authToken, this.toIso(start), this.toIso(end))
        const root = this.extractRoot(resp)
        this.rows = Array.isArray(root?.data) ? root.data : Array.isArray(root) ? root : []
      } catch (e) {
        this.error = e?.message || "Failed to load"
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    authToken:          { immediate: true, handler() { this.fetchData() } },
    selectedTimeframe() { this.fetchData() }
  }
}
</script>

<style scoped>
.ci-accent--ctc { --accent-color: #f59e0b; }

.ci-summary-group {
  min-height: 0;
  height: 100%;
  border: 1px solid var(--border-ltr);
  border-radius: 12px;
  background: var(--background);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.ci-summary-title {
  padding: 10px 12px;
  border-bottom: 1px solid var(--accent-color);
  background: rgba(245,158,11,0.08);
  font-weight: 950;
  font-size: 12px;
  color: #92400e;
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: var(--card-header-height, 50px);
}

.ci-summary-head { display: flex; align-items: center; gap: 8px; margin-left: auto; }
.ci-summary-head-title { font-weight: 950; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.ci-trend-select {
  height: 26px;
  border-radius: 8px;
  padding: 0 8px;
  border: 1px solid rgba(15,23,42,0.12);
  background: rgba(15,23,42,0.02);
  font-weight: 950;
  font-size: 11px;
  color: var(--text-primary-on-background);
  cursor: pointer;
}

.ci-kpi-row   { display: inline-flex; align-items: baseline; gap: 4px; }
.ci-kpi-value { font-size: 18px; font-weight: 950; color: #92400e; line-height: 1; }
.ci-kpi-label { font-size: 11px; font-weight: 900; opacity: 0.7; }

/* ── Scrollable outer list ───────────────────────────────────────────── */
.ci-summary-cards {
  flex: 1 1 auto;
  min-height: 0;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
}

.ci-summary-loading {
  height: 74px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 950;
  color: var(--text-secondary-on-background);
}
.ci-empty-inline { padding: 18px 10px; text-align: center; font-weight: 950; color: var(--text-secondary-on-background); font-size: 12px; }
.ci-empty-inline--error { color: #b91c1c; }

/* ── UW group — NO fixed height, NO overflow hidden ─────────────────── */
.ci-uw-group {
  border: 1px solid rgba(15,23,42,0.09);
  border-radius: 9px;
  flex-shrink: 0;
}

.ci-uw-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  background: rgba(245,158,11,0.04);
  border: none;
  border-radius: 9px;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
}
.ci-uw-header:hover { background: rgba(245,158,11,0.10); }

.ci-uw-left { display: flex; align-items: center; gap: 6px; min-width: 0; flex: 1; }
.ci-uw-chevron {
  font-size: 14px;
  font-weight: 950;
  color: var(--text-secondary-on-background);
  transition: transform 0.18s ease;
  line-height: 1;
  flex-shrink: 0;
}
.ci-uw-chevron--open { transform: rotate(90deg); }
.ci-uw-name { font-size: 12px; font-weight: 950; color: var(--text-primary-on-background); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.ci-uw-pills { display: flex; align-items: center; gap: 4px; flex-shrink: 0; flex-wrap: nowrap; }
.ci-uw-total { font-size: 13px; font-weight: 950; color: var(--text-primary-on-background); margin-left: 4px; }

/* ── Loan type pills ─────────────────────────────────────────────────── */
.ci-type-pill {
  font-size: 9.5px;
  font-weight: 950;
  padding: 1px 6px;
  border-radius: 999px;
  border: 1px solid transparent;
  white-space: nowrap;
  flex-shrink: 0;
}
.ci-type-pill--fha  { background: rgba(37,99,235,0.10);   border-color: rgba(37,99,235,0.25);   color: #1d4ed8; }
.ci-type-pill--conv { background: rgba(16,185,129,0.10);  border-color: rgba(16,185,129,0.25);  color: #065f46; }
.ci-type-pill--va   { background: rgba(124,58,237,0.10);  border-color: rgba(124,58,237,0.25);  color: #5b21b6; }
.ci-type-pill--usda { background: rgba(234,88,12,0.10);   border-color: rgba(234,88,12,0.25);   color: #9a3412; }
.ci-type-pill--other{ background: rgba(100,116,139,0.10); border-color: rgba(100,116,139,0.25); color: #475569; }

/* ── Loan rows — capped at ~6 rows then scroll ───────────────────────── */
.ci-uw-loans {
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(15,23,42,0.07);
  max-height: 252px;   /* ~6 rows × 42px each */
  overflow-y: auto;
}

.ci-loan-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 12px 7px 22px;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(15,23,42,0.05);
  cursor: pointer;
  text-align: left;
  width: 100%;
  flex-shrink: 0;
  transition: background 0.12s;
}
.ci-loan-row:last-child { border-bottom: none; }
.ci-loan-row:hover { background: rgba(245,158,11,0.06); }

.ci-loan-left { display: flex; flex-direction: column; gap: 3px; min-width: 0; flex: 1; }
.ci-loan-borrower { font-size: 11.5px; font-weight: 950; color: var(--text-primary-on-background); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ci-loan-meta { display: flex; align-items: center; gap: 5px; }
.ci-loan-sub { font-size: 10px; font-weight: 800; color: var(--text-secondary-on-background); opacity: 0.7; }

.ci-metric-pill { display: inline-flex; align-items: baseline; gap: 4px; flex-shrink: 0; }
.ci-pill-label  { font-size: 10px; font-weight: 900; color: var(--text-secondary-on-background); }
.ci-pill-value  { font-size: 13px; font-weight: 950; color: var(--text-primary-on-background); }

/* ── Modal ───────────────────────────────────────────────────────────── */
.ci-modal {
  position: fixed;
  inset: 0;
  z-index: 25000;
  background: rgba(15,23,42,0.38);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
}

.ci-modal__panel {
  width: min(860px, calc(100vw - 28px));
  max-height: min(80vh, 820px);
  background: var(--background);
  border: 1px solid rgba(0,0,0,0.10);
  border-radius: 16px;
  box-shadow: 0 28px 70px rgba(0,0,0,0.30);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ci-modal__panel--wide { width: min(960px, calc(100vw - 28px)); }

.ci-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0,0,0,0.08);
  flex-shrink: 0;
}
.ci-modal__head-left    { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.ci-modal__title        { font-size: 14px; font-weight: 950; color: var(--text-primary-on-background); }
.ci-modal__meta         { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ci-modal__head-actions { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }

.ci-modal__close {
  border: 1px solid rgba(0,0,0,0.08);
  background: rgba(0,0,0,0.02);
  border-radius: 10px;
  height: 32px;
  width: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
}
.ci-modal__body { padding: 16px; overflow: auto; display: flex; flex-direction: column; gap: 0; }

.ci-drill-section { margin-bottom: 16px; }
.ci-drill-section-title {
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--text-secondary-on-background);
  opacity: 0.65;
  margin-bottom: 7px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(15,23,42,0.07);
}

.ci-drill-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.ci-drill-chip {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid rgba(15,23,42,0.08);
  background: rgba(15,23,42,0.02);
  min-width: 100px;
}
.ci-drill-chip--success { background: rgba(16,185,129,0.06); border-color: rgba(16,185,129,0.2); }
.ci-drill-chip--doc     { min-width: unset; background: rgba(37,99,235,0.04); border-color: rgba(37,99,235,0.15); }
.ci-drill-chip__k       { font-size: 10px; font-weight: 950; text-transform: uppercase; letter-spacing: 0.3px; opacity: 0.5; }
.ci-drill-chip__v       { font-size: 14px; font-weight: 950; color: var(--text-primary-on-background); }
.ci-drill-chip__v--note { font-size: 12px; color: #92400e; }
.ci-drill-chip__v--sm   { font-size: 12px; }

.ci-detail-list { display: flex; flex-direction: column; gap: 5px; }
.ci-detail-card {
  border: 1px solid rgba(0,0,0,0.06);
  background: rgba(0,0,0,0.02);
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
  width: 100%;
}
.ci-detail-card--compact { padding: 7px 10px; }
.ci-detail-card--click   { cursor: pointer; transition: background 0.12s; }
.ci-detail-card--click:hover { background: rgba(37,99,235,0.05); }
.ci-drill-chevron { font-size: 14px; font-weight: 950; color: var(--text-secondary-on-background); opacity: 0.5; }

.ci-detail-title-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.ci-detail-title { font-weight: 950; font-size: 12px; color: var(--text-primary-on-background); }
.ci-detail-subrow { margin-top: 4px; }
.ci-detail-text     { font-size: 12px; line-height: 1.5; color: var(--text-secondary-on-background); margin-top: 4px; }
.ci-detail-text--sm { font-size: 11.5px; }
.ci-detail-block    { background: rgba(15,23,42,0.02); border: 1px solid rgba(15,23,42,0.06); border-radius: 10px; padding: 10px 12px; }

.ci-sub-pill        { display: inline-flex; align-items: baseline; gap: 4px; }
.ci-sub-pill__k     { font-size: 10px; font-weight: 950; opacity: 0.55; text-transform: uppercase; letter-spacing: 0.3px; }
.ci-sub-pill__v     { font-size: 12px; font-weight: 900; }
.ci-sub-pill--blue .ci-sub-pill__v { color: #1d4ed8; font-weight: 950; }

.ci-open-swift {
  border: 1px solid rgba(37,99,235,0.35);
  background: rgba(37,99,235,0.08);
  border-radius: 8px;
  height: 28px;
  padding: 0 12px;
  font-weight: 950;
  font-size: 11px;
  cursor: pointer;
  color: #1d4ed8;
  flex-shrink: 0;
  white-space: nowrap;
}
</style>